import { addBreadcrumb } from "@/lib/error-tracking";

const CONNECTING = typeof WebSocket !== "undefined" ? WebSocket.CONNECTING : 0;
const OPEN = typeof WebSocket !== "undefined" ? WebSocket.OPEN : 1;
const CLOSING = typeof WebSocket !== "undefined" ? WebSocket.CLOSING : 2;
const CLOSED = typeof WebSocket !== "undefined" ? WebSocket.CLOSED : 3;

type OfflineWebSocket = WebSocket & { markClosed: () => void };

function createOfflineWebSocket(urlString: string): OfflineWebSocket {
  const state: { readyState: 0 | 1 | 2 | 3 } = { readyState: CONNECTING };
  return {
    CONNECTING,
    OPEN,
    CLOSING,
    CLOSED,
    get readyState() {
      return state.readyState;
    },
    markClosed: () => {
      state.readyState = CLOSED;
    },
    url: urlString,
    bufferedAmount: 0,
    extensions: "",
    protocol: "",
    binaryType: "blob",
    onopen: null,
    onerror: null,
    onmessage: null,
    onclose: null,
    send: () => {},
    close: () => {},
    addEventListener: () => {},
    removeEventListener: () => {},
    dispatchEvent: () => false,
  };
}

function createCloseEvent(): CloseEvent {
  try {
    return new CloseEvent("close", {
      code: 1006,
      reason: "",
      wasClean: false,
    });
  } catch {
    return Object.assign(new Event("close"), {
      code: 1006,
      reason: "",
      wasClean: false,
    });
  }
}

function degradeToOffline(url: string | URL, error: unknown): OfflineWebSocket {
  const urlString = String(url);

  addBreadcrumb({
    category: "websocket",
    message: `WebSocket construction failed for ${urlString}`,
    level: "warning",
    data: {
      url: urlString,
      error: error instanceof Error ? error.message : String(error),
    },
  });

  console.warn(
    "[Convex] WebSocket construction failed — degrading to offline mode:",
    urlString,
  );

  const offline = createOfflineWebSocket(urlString);

  queueMicrotask(() => {
    offline.markClosed();

    if (typeof offline.onerror === "function") {
      offline.onerror(new Event("error"));
    }
    if (typeof offline.onclose === "function") {
      offline.onclose(createCloseEvent());
    }
  });

  return offline;
}

export function safeWebSocketConstructor(): typeof WebSocket | undefined {
  if (typeof WebSocket === "undefined") return undefined;
  return new Proxy(WebSocket, {
    construct(nativeWebSocket, args: [string | URL, (string | string[])?]) {
      try {
        return Reflect.construct(nativeWebSocket, args);
      } catch (error) {
        return degradeToOffline(args[0], error);
      }
    },
  });
}
